<template>
	<div class="flex flex-col">
		<InputText v-if="disabled" :label="props.title" type="text" label-text-size="text-sm" :input-height="props.inputHeight"
					  font-weight="font-normal" v-model="selectedItem" :disabled="true" :icon="icon"/>

		<Combobox v-else as="div" v-model="selectedItem">
			<div ref="comboboxRef">
				<ComboboxLabel v-if="props.title" class="leading-6 mb-3 block text-label" :class="labelCustomClass">{{ props.title }}</ComboboxLabel>
				<div class="relative">
					<div class="group">
						<ComboboxInput :maxLength="props.maxLength" autocomplete="off" :displayValue="(item) => item?.name"
											class="block w-full font-medium rounded-lg bg-neutral py-4 pl-4 pr-12 text-label outline-none border border-neutral placeholder:text-placeholder focus:ring-1 focus:ring-primary-bold-hover focus:border-primary-bold-hover group-hover:ring-1 group-hover:ring-primary-bold-hover group-hover:border-primary-bold-hover"
											@change="query = $event.target.value" :placeholder="props.placeholder"
											:class="props.inputHeight + (hasError() ? ' border-error' : ' border-neutral') + (props.readonly ? ' cursor-pointer' : '')" />
						<div class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
								<svg v-if="isLoading" class="animate-spin h-5 w-5 text-primary fill-icon-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
									<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"></circle>
									<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
								</svg>
								<MagnifyingGlassIcon v-else class="size-5 group-hover:text-primary-hover" aria-label="Magnifying Glass" />
						</div>
					</div>

					<ComboboxOptions v-if="searchResults.length > 0"
										  class="absolute z-99 mt-2 w-full overflow-auto rounded-lg bg-neutral mb-2 text-base font-medium ring-1 ring-primary-bold-hover border border-primary-bold-hover focus:outline-none sm:text-sm"
										  :class="[{ 'top-full': !showOptionsOnTop, 'bottom-full': showOptionsOnTop }, maxSearchResultsHeight]">
						<ComboboxOption v-for="item in searchResults" :key="item.id" :value="item" as="template" v-slot="{ active, selected }">
							<li :class="['relative cursor-default select-none py-2 pl-3 pr-4', active ? 'bg-primary-subtle text-label outline-none' : 'text-placeholder']">
								<span :class="['block truncate whitespace-pre-wrap', selected && 'text-label']">
									{{ item.name }}
								</span>
							</li>
						</ComboboxOption>
					</ComboboxOptions>
				</div>
			</div>
		</Combobox>
		<ValidationError :errors="props.errors" :field="props.errorField" class="mt-2 ml-3.5" />
	</div>
</template>

<script setup lang="js">

import { computed, ref, defineModel, watch } from 'vue'
import { MagnifyingGlassIcon } from '@heroicons/vue/20/solid'
import {
	Combobox,
	ComboboxInput,
	ComboboxLabel,
	ComboboxOption,
	ComboboxOptions
} from '@headlessui/vue'
import ValidationError from '@/components/Forms/InputError.vue'
import { api, ApiGatewayResponse } from '@/api/ApiGateway.js'
import InputText from '@/components/Forms/InputText.vue'
import { refDebounced } from '@vueuse/core'

const props = defineProps({
	id: { type: String, required: true },
	items: { type: Array, default: () => [] },
	title: { type: String, default: '' },
	placeholder: { type: String, default: '' },
	showOptionsOnTop: { type: Boolean, default: false },
	inputHeight: { type: String, default: '' },
	labelTextSize: { type: String, default: '' },
	labelClass: { type: String, default: '' },//AJL
	errors: { type: ApiGatewayResponse, default: null },
	errorField: { type: String, default: null },
	readonly: { type: Boolean, default: false },
	disabled: { type: Boolean, default: false },
	icon: { type: String, default: 'none' },
	maxLength: { type: Number, default: 255 },
	endpoint: { type: String, default: null },
	maxSearchResultsHeight: { type: String, default: 'max-h-40' },
	limit: { type: Number, default: 10 }, //Limit number of results
})

const selectedItem = defineModel()

const labelCustomClass = computed(() =>
{
	return [props.labelClass, props.labelTextSize].join(' ')
})

const query = ref('')
const searchResults = ref([])
const comboboxRef = ref(null)
const isLoading = ref(false)

const debouncedQuery = refDebounced(query, 350)
watch(debouncedQuery, (name) => {
	if (!name) {
		searchResults.value = []
		selectedItem.value = null
		isLoading.value = false
		return
	}

	isLoading.value = true

	api.get(props.endpoint + name + '&limit=' + props.limit).then((response) => {
		if (response.hasError() || !response.body()) {
			isLoading.value = false
			return
		}

		const records = response.body().data;

		//Add a "data" property to your returned value to include extra data beyond id and name (see Edit Org Groups)
		searchResults.value = records.map((record) => {
			return {
				id: record.id,
				name: record.name,
				data: record.data
			}
		})

		isLoading.value = false
	})
})

function hasError()
{
	return props.errors?.validationErrors()?.find((error) => error.source === props.errorField) != null
}

</script>